import { useEffect, useState } from "react";
import { AlertCircle, Download, FileText } from "lucide-react";

import { fetchStreamUrl, type ApiFileRef } from "../lib/api";
import { useAuthedBlobUrl } from "../lib/hooks";
import { formatBytes } from "../lib/format";
import { gradientCSSFor } from "../lib/avatarGradient";

// Source-code-style filetypes the exporter labels but that don't
// always carry a `text/*` mimetype.
const TEXT_FILETYPES = new Set([
  "python",
  "csv",
  "text",
  "javascript",
  "typescript",
  "json",
  "markdown",
  "html",
  "css",
  "shell",
]);

const TEXT_PREVIEW_LIMIT = 600;

interface AttachmentProps {
  /** team_id needed to build the API path. */
  teamID: string;
  fileRef: ApiFileRef;
}

// Pass 2.2: bytes come from the service via an authed fetch. The
// browser can't put the bearer token on a plain <img src> URL, so
// we fetch the blob ourselves and wrap it in
// URL.createObjectURL — the resulting blob: URL goes into <img
// src>, <video src>, etc. with no auth header needed at the
// browser-render layer. The hook handles cleanup (revokeObjectURL
// on unmount).
export function Attachment({ teamID, fileRef }: AttachmentProps) {
  // Video streams via a range-capable, token'd <video src> (served from
  // our own domain — disk or a Drive byte-range proxy) instead of
  // blob-downloading the whole file, which dies on large media. See
  // StreamingVideo below. Everything else keeps the authed-blob path.
  if ((fileRef.mimetype ?? "").startsWith("video/")) {
    return <StreamingVideo teamID={teamID} fileRef={fileRef} />;
  }
  return <BlobAttachment teamID={teamID} fileRef={fileRef} />;
}

function BlobAttachment({ teamID, fileRef }: AttachmentProps) {
  const { url: blobUrl, error: fetchError } = useAuthedBlobUrl(
    teamID,
    fileRef.id,
  );
  const errorMsg = fetchError ? fetchError.message : null;

  const displayName = fileRef.name || "(unnamed)";

  if (errorMsg) {
    return (
      <div
        data-testid={`attachment-error-${fileRef.id}`}
        data-attachment-kind="error"
        className="my-2 px-3 py-2 bg-theme-error-bg border-l-2 border-theme-error-border text-xs text-theme-error-text inline-flex items-center gap-2"
      >
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
        <span className="font-mono">
          Failed to load: {displayName} — {errorMsg}
        </span>
      </div>
    );
  }

  if (!blobUrl) {
    return (
      <div
        data-testid={`attachment-loading-${fileRef.id}`}
        data-attachment-kind="loading"
        className="my-2 px-3 py-2 bg-theme-bg-chip-subtle border border-theme-border-default rounded-sm text-xs text-theme-text-tertiary font-mono inline-block"
      >
        Loading {displayName}…
      </div>
    );
  }

  const mime = fileRef.mimetype ?? "";
  const filetype = fileRef.filetype ?? "";
  const isImage = mime.startsWith("image/");
  const isPDF = mime === "application/pdf";
  const isText = mime.startsWith("text/") || TEXT_FILETYPES.has(filetype);

  if (isImage) {
    return (
      <div
        className="my-1 block w-fit"
        data-testid={`attachment-image-${fileRef.id}`}
        data-attachment-kind="image"
      >
        <a href={blobUrl} target="_blank" rel="noreferrer">
          <img
            src={blobUrl}
            alt={displayName}
            className="block max-w-md max-h-96 object-contain rounded-lg border border-white/[0.09]"
          />
        </a>
        <div className="text-[10px] font-mono text-theme-text-tertiary mt-1">
          {displayName}
        </div>
      </div>
    );
  }

  if (isPDF) {
    return (
      <div
        className="my-1 flex w-fit max-w-full min-w-0 items-center gap-3 pl-2 pr-3.5 py-2 bg-white/[0.04] border border-white/[0.09] rounded-xl"
        data-testid={`attachment-pdf-${fileRef.id}`}
        data-attachment-kind="pdf"
      >
        <FileTypeChip name={displayName} fallback="PDF" />
        <div className="min-w-0">
          <a
            href={blobUrl}
            target="_blank"
            rel="noreferrer"
            className="block text-sm font-semibold text-theme-text-primary hover:text-theme-accent-link truncate max-w-xs"
            title={displayName}
          >
            {displayName}
          </a>
          <span className="font-mono text-[10px] text-theme-text-muted uppercase tracking-wide">
            pdf{fileRef.size ? ` · ${formatBytes(fileRef.size)}` : ""}
          </span>
        </div>
        <a
          href={blobUrl}
          download={displayName}
          className="text-theme-text-tertiary hover:text-theme-text-primary"
          title="Download"
          aria-label={`Download ${displayName}`}
        >
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
        </a>
      </div>
    );
  }

  const isAudio = mime.startsWith("audio/");
  if (isAudio) {
    return (
      <div
        className="my-1 flex w-full max-w-md min-w-0 flex-col gap-2 px-3 py-2.5 bg-white/[0.04] border border-white/[0.09] rounded-xl"
        data-testid={`attachment-audio-${fileRef.id}`}
        data-attachment-kind="audio"
      >
        <div className="flex items-center gap-3">
          <FileTypeChip name={displayName} fallback="AUD" />
          <div className="min-w-0 flex-1">
            <span
              className="block text-sm font-semibold text-theme-text-primary truncate max-w-xs"
              title={displayName}
            >
              {displayName}
            </span>
            <span className="font-mono text-[10px] text-theme-text-muted uppercase tracking-wide">
              audio{fileRef.size ? ` · ${formatBytes(fileRef.size)}` : ""}
            </span>
          </div>
          <a
            href={blobUrl}
            download={displayName}
            className="text-theme-text-tertiary hover:text-theme-text-primary flex-shrink-0"
            title="Download"
            aria-label={`Download ${displayName}`}
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
          </a>
        </div>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- archived audio ships no captions */}
        <audio src={blobUrl} controls preload="metadata" className="h-9 w-full min-w-0 max-w-[320px]" />
      </div>
    );
  }

  if (isText) {
    return (
      <TextPreview
        blobUrl={blobUrl}
        displayName={displayName}
        fileRef={fileRef}
      />
    );
  }

  return (
    <div
      className="my-1 flex w-fit max-w-full min-w-0 items-center gap-3 pl-2 pr-3.5 py-2 bg-white/[0.04] border border-white/[0.09] rounded-xl"
      data-testid={`attachment-download-${fileRef.id}`}
      data-attachment-kind="download"
    >
      <FileTypeChip name={displayName} fallback="FILE" />
      <div className="min-w-0">
        <a
          href={blobUrl}
          download={displayName}
          className="block text-sm font-semibold text-theme-text-primary hover:text-theme-accent-link truncate max-w-xs"
          title={displayName}
        >
          {displayName}
        </a>
        <span className="font-mono text-[10px] text-theme-text-muted uppercase tracking-wide">
          {formatBytes(fileRef.size ?? 0)}
        </span>
      </div>
      <Download
        className="w-3.5 h-3.5 text-theme-text-tertiary flex-shrink-0"
        strokeWidth={1.5}
        aria-hidden
      />
    </div>
  );
}

// Video plays via a token'd, range-capable <video src> pointed straight
// at our service (which streams from disk or proxies a Drive byte-range).
// No eager blob-download of the whole file — that's what killed large
// videos with "Failed to fetch". No Drive URL is exposed to the browser.
function StreamingVideo({ teamID, fileRef }: AttachmentProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const displayName = fileRef.name || "(unnamed)";

  useEffect(() => {
    const ctrl = new AbortController();
    setUrl(null);
    setError(null);
    fetchStreamUrl(teamID, fileRef.id, ctrl.signal)
      .then(({ url }) => setUrl(url))
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => ctrl.abort();
  }, [teamID, fileRef.id]);

  if (error) {
    return (
      <div
        data-testid={`attachment-error-${fileRef.id}`}
        data-attachment-kind="error"
        className="my-2 px-3 py-2 bg-theme-error-bg border-l-2 border-theme-error-border text-xs text-theme-error-text inline-flex items-center gap-2"
      >
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
        <span className="font-mono">
          Failed to load: {displayName} — {error}
        </span>
      </div>
    );
  }

  if (!url) {
    return (
      <div
        data-testid={`attachment-loading-${fileRef.id}`}
        data-attachment-kind="loading"
        className="my-2 px-3 py-2 bg-theme-bg-chip-subtle border border-theme-border-default rounded-sm text-xs text-theme-text-tertiary font-mono inline-block"
      >
        Loading {displayName}…
      </div>
    );
  }

  return (
    <div
      className="my-1 w-fit"
      data-testid={`attachment-video-${fileRef.id}`}
      data-attachment-kind="video"
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- archive videos do not ship captions */}
      <video
        src={url}
        controls
        preload="metadata"
        className="max-w-md max-h-96 rounded-lg border border-white/[0.09]"
      />
      <div className="text-[10px] font-mono text-theme-text-tertiary mt-1">
        {displayName}
      </div>
    </div>
  );
}

// Aurora — small gradient square showing the file extension, keyed by
// the extension string so e.g. every .pdf gets the same gradient.
function FileTypeChip({ name, fallback }: { name: string; fallback: string }) {
  const ext = (name.split(".").pop() || fallback).slice(0, 4).toUpperCase();
  return (
    <span
      aria-hidden
      style={{ background: gradientCSSFor(ext) }}
      className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 font-mono text-[8.5px] font-bold tracking-wider text-white"
    >
      {ext}
    </span>
  );
}

interface TextPreviewProps {
  blobUrl: string;
  displayName: string;
  fileRef: ApiFileRef;
}

function TextPreview({ blobUrl, displayName, fileRef }: TextPreviewProps) {
  const [text, setText] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(blobUrl)
      .then((r) => r.text())
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch(() => {
        if (!cancelled) setText("(failed to read)");
      });
    return () => {
      cancelled = true;
    };
  }, [blobUrl]);

  const overflow = text != null && text.length > TEXT_PREVIEW_LIMIT;
  const preview =
    text == null ? "" : expanded ? text : text.slice(0, TEXT_PREVIEW_LIMIT);

  return (
    <div
      className="my-2 bg-theme-bg-chip-subtle border border-theme-border-default rounded-sm max-w-xl"
      data-testid={`attachment-text-${fileRef.id}`}
      data-attachment-kind="text"
    >
      <div className="px-3 py-2 border-b border-theme-border-default flex items-center gap-2">
        <FileText
          className="w-3.5 h-3.5 text-theme-text-secondary flex-shrink-0"
          strokeWidth={1.5}
          aria-hidden
        />
        <span
          className="text-xs font-mono text-theme-text-secondary truncate"
          title={displayName}
        >
          {displayName}
        </span>
        {fileRef.filetype && (
          <span className="text-[10px] font-mono text-theme-text-tertiary uppercase tracking-wider flex-shrink-0">
            {fileRef.filetype}
          </span>
        )}
        <a
          href={blobUrl}
          download={displayName}
          className="ml-auto text-theme-text-tertiary hover:text-theme-text-primary flex-shrink-0"
          title="Download"
          aria-label={`Download ${displayName}`}
        >
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
        </a>
      </div>
      <pre
        className={[
          "px-3 py-2 text-[11px] font-mono text-theme-text-primary whitespace-pre-wrap overflow-x-auto",
          expanded ? "max-h-96 overflow-y-auto scrollbar-thin" : "",
        ].join(" ")}
      >
        {preview}
        {overflow && !expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            data-testid={`attachment-text-expand-${fileRef.id}`}
            className="block mt-2 text-theme-accent-link hover:underline"
          >
            …show all ({text!.length.toLocaleString()} chars)
          </button>
        )}
      </pre>
    </div>
  );
}
